import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  Typography, CircularProgress, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox, Chip, Divider, Tooltip
} from '@mui/material';
import { AutoAwesome, CheckCircle } from '@mui/icons-material';
import { contractsApi } from '../../services/api';
import type { Contract } from '../../types';

const FIELD_LABELS: Record<string, string> = {
  contract_name: 'Contract Name',
  contract_number: 'Contract Number',
  contract_type: 'Contract Type',
  counterparty_name: 'Counterparty Name',
  counterparty_type: 'Counterparty Type',
  counterparty_company: 'Company',
  counterparty_contact_name: 'Contact Name',
  counterparty_contact_email: 'Contact Email',
  counterparty_contact_title: 'Contact Title',
  address: 'Address',
  effective_date: 'Effective Date',
  start_date: 'Start Date',
  expiration_date: 'Expiration Date',
  end_date: 'End Date',
  term_length_months: 'Term Length (months)',
  auto_renews: 'Auto-Renews',
  renewal_term_months: 'Renewal Term (months)',
  notice_period_days: 'Notice Period (days)',
  contract_value: 'Contract Value',
  currency: 'Currency',
  payment_terms_days: 'Payment Terms (days)',
  payment_terms: 'Payment Terms',
  billing_frequency: 'Billing Frequency',
  governing_law: 'Governing Law',
  jurisdiction: 'Jurisdiction',
  venue_jurisdiction: 'Venue / Jurisdiction',
  assignment_rights: 'Assignment Rights',
  description: 'Description',
  scope_summary: 'Scope Summary',
  counterparty_signatory_name: 'Counterparty Signatory',
  counterparty_signatory_title: 'Counterparty Title',
  counterparty_signature_date: 'Counterparty Signature Date',
  hyperlink_signatory_name: 'HyperLink Signatory',
  hyperlink_signatory_title: 'HyperLink Title',
  hyperlink_signature_date: 'HyperLink Signature Date',
};

function formatValue(field: string, value: any): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function isDifferent(extracted: any, current: any): boolean {
  if (extracted === null || extracted === undefined) return false;
  if (typeof extracted === 'boolean') return Boolean(current) !== extracted;
  return String(current ?? '').trim() !== String(extracted).trim();
}

interface Props {
  open: boolean;
  onClose: () => void;
  contract: Contract;
  onApplied: () => void;
}

export default function ClaudeExtractDialog({ open, onClose, contract, onApplied }: Props) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extracted, setExtracted] = useState<Record<string, any> | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (open) {
      setExtracted(null);
      setError(null);
      setSelected(new Set());
      setApplied(false);
      runExtraction();
    }
  }, [open]);

  async function runExtraction() {
    setLoading(true);
    setError(null);
    try {
      const res = await contractsApi.claudeExtract(contract.id);
      const data: Record<string, any> = res.data.extracted;

      // Pre-select fields that differ from current contract values
      const autoSelected = new Set<string>();
      for (const [field, value] of Object.entries(data)) {
        if (value !== null && value !== undefined && isDifferent(value, (contract as any)[field])) {
          autoSelected.add(field);
        }
      }
      setExtracted(data);
      setSelected(autoSelected);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Extraction failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function toggleField(field: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  }

  function toggleAll() {
    if (!extracted) return;
    const allFields = Object.keys(extracted).filter(f => FIELD_LABELS[f]);
    if (selected.size === allFields.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allFields));
    }
  }

  async function handleApply() {
    if (!extracted || selected.size === 0) return;
    setApplying(true);
    try {
      const updates: Record<string, any> = {};
      selected.forEach(field => {
        updates[field] = extracted[field];
      });
      await contractsApi.update(contract.id, updates);
      setApplied(true);
      onApplied();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to apply changes.');
    } finally {
      setApplying(false);
    }
  }

  const extractedFields = extracted
    ? Object.entries(extracted).filter(([f]) => FIELD_LABELS[f])
    : [];

  const changedCount = extractedFields.filter(([f, v]) => isDifferent(v, (contract as any)[f])).length;

  return (
    <Dialog open={open} onClose={loading || applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesome color="primary" />
          <Typography variant="h6">Extract with Claude AI</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Claude will analyse the uploaded contract document and suggest field values.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
            <CircularProgress size={48} />
            <Typography color="text.secondary">
              Analysing contract with Claude Opus&hellip;
            </Typography>
            <Typography variant="caption" color="text.disabled">
              This may take 15–45 seconds for complex documents.
            </Typography>
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" action={
            <Button size="small" onClick={runExtraction}>Retry</Button>
          }>
            {error}
          </Alert>
        )}

        {applied && (
          <Alert severity="success" icon={<CheckCircle />}>
            {selected.size} field{selected.size !== 1 ? 's' : ''} applied successfully.
          </Alert>
        )}

        {extracted && !loading && !applied && (
          <>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label={`${extractedFields.length} fields extracted`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                {changedCount > 0 && (
                  <Chip
                    label={`${changedCount} differ from current`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
              <Button size="small" onClick={toggleAll}>
                {selected.size === extractedFields.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell><Typography variant="caption" fontWeight={600}>Field</Typography></TableCell>
                    <TableCell><Typography variant="caption" fontWeight={600}>Current Value</Typography></TableCell>
                    <TableCell><Typography variant="caption" fontWeight={600}>Extracted Value</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {extractedFields.map(([field, value]) => {
                    const current = (contract as any)[field];
                    const different = isDifferent(value, current);
                    return (
                      <TableRow
                        key={field}
                        hover
                        selected={selected.has(field)}
                        onClick={() => toggleField(field)}
                        sx={{ cursor: 'pointer', opacity: selected.has(field) ? 1 : 0.6 }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.has(field)}
                            size="small"
                            onClick={e => e.stopPropagation()}
                            onChange={() => toggleField(field)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{FIELD_LABELS[field] || field}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatValue(field, current)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={different ? 'Differs from current value' : 'Same as current value'}>
                            <Typography
                              variant="body2"
                              fontWeight={different ? 600 : 400}
                              color={different ? 'primary.main' : 'text.secondary'}
                              sx={{ wordBreak: 'break-word', maxWidth: 280 }}
                            >
                              {formatValue(field, value)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {extractedFields.length === 0 && (
              <Box py={4} textAlign="center">
                <Typography color="text.secondary">
                  No fields could be extracted from this document.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={applying}>
          {applied ? 'Close' : 'Cancel'}
        </Button>
        {extracted && !applied && (
          <Button
            variant="contained"
            startIcon={applying ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
            onClick={handleApply}
            disabled={selected.size === 0 || applying}
          >
            {applying ? 'Applying…' : `Apply ${selected.size} Field${selected.size !== 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
